import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getExamById, getQuestionsDetail } from '../../api';
import toast, { Toaster } from 'react-hot-toast';
import { 
  ArrowLeftIcon,
  EyeIcon,
  ClockIcon,
  AcademicCapIcon,
  CalendarIcon,
  TrophyIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ListBulletIcon,
  TagIcon
} from '@heroicons/react/24/outline';

const ViewExam = () => {
  const { eid } = useParams();
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log('📥 Fetching exam details for eid:', eid);
        
        const [examData, questionsData] = await Promise.all([
          getExamById(eid),
          getQuestionsDetail(eid)
        ]);
        
        console.log('📊 Exam data:', examData);
        console.log('📝 Questions data:', questionsData);
        
        setExam(examData);
        setQuestions(Array.isArray(questionsData) ? questionsData : []);
        toast.success('Exam details loaded successfully');
      } catch (error) {
        console.error('❌ Error fetching data:', error);
        toast.error('Failed to load exam details');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [eid]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex justify-center items-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading exam details...</p>
        </div>
      </div>
    );
  }

  if (!exam) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-6">
        <div className="max-w-2xl mx-auto mt-20">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
            <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <DocumentTextIcon className="h-12 w-12 text-red-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-800 mb-4">Exam Not Found</h2>
            <p className="text-gray-600 mb-8">The exam you're looking for doesn't exist.</p>
            <button 
              onClick={() => navigate('/admin/exam')}
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-4 rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transform hover:scale-105 transition-all shadow-md"
            >
              <ArrowLeftIcon className="h-5 w-5" />
              <span>Back to All Exams</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-6">
      <Toaster position="top-right" />

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center">
                <EyeIcon className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">View Exam</h1>
                <p className="text-sm text-gray-500">Exam ID: {eid}</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/admin/exam')}
              className="flex items-center space-x-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all border border-gray-300"
            >
              <ArrowLeftIcon className="h-5 w-5" />
              <span>Back to Exams</span>
            </button>
          </div>
        </div>

        {/* Exam Details Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
            <DocumentTextIcon className="h-6 w-6 mr-2 text-blue-600" />
            Exam Details
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Exam Name */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-5 rounded-xl border-2 border-blue-200">
              <div className="flex items-center space-x-2 mb-2">
                <AcademicCapIcon className="h-5 w-5 text-blue-600" />
                <label className="text-xs font-semibold text-blue-700 uppercase tracking-wider">
                  Exam Name
                </label>
              </div>
              <p className="text-lg font-bold text-gray-800">{exam.name}</p>
            </div>

            {/* Class */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-5 rounded-xl border-2 border-purple-200">
              <div className="flex items-center space-x-2 mb-2">
                <AcademicCapIcon className="h-5 w-5 text-purple-600" />
                <label className="text-xs font-semibold text-purple-700 uppercase tracking-wider">
                  Class
                </label>
              </div>
              <p className="text-lg font-bold text-gray-800">Class {exam.class}</p>
            </div>

            {/* Status */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-5 rounded-xl border-2 border-green-200">
              <div className="flex items-center space-x-2 mb-2">
                <CheckCircleIcon className="h-5 w-5 text-green-600" />
                <label className="text-xs font-semibold text-green-700 uppercase tracking-wider">
                  Status
                </label>
              </div>
              <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-bold ${
                exam.estatus === '1' 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                {exam.estatus === '1' ? '✅ Published' : '📝 Draft'}
              </span>
            </div>

            {/* Duration */}
            <div className="bg-gray-50 p-5 rounded-xl border border-gray-200">
              <div className="flex items-center space-x-2 mb-2">
                <ClockIcon className="h-5 w-5 text-gray-600" />
                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Duration
                </label>
              </div>
              <p className="text-lg font-bold text-gray-800">{exam.duration} minutes</p>
            </div>

            {/* Total Marks */}
            <div className="bg-gray-50 p-5 rounded-xl border border-gray-200">
              <div className="flex items-center space-x-2 mb-2">
                <TrophyIcon className="h-5 w-5 text-gray-600" />
                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Total Marks
                </label>
              </div>
              <p className="text-lg font-bold text-gray-800">{exam.tmarks}</p>
            </div>

            {/* Date */}
            <div className="bg-gray-50 p-5 rounded-xl border border-gray-200">
              <div className="flex items-center space-x-2 mb-2">
                <CalendarIcon className="h-5 w-5 text-gray-600" />
                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Exam Date
                </label>
              </div>
              <p className="text-lg font-bold text-gray-800">{exam.sdate || exam.date || 'N/A'}</p>
            </div>
          </div>
        </div>

        {/* Questions Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-800 flex items-center">
              <ListBulletIcon className="h-6 w-6 mr-2 text-green-600" />
              Questions ({questions.length})
            </h2>
            <span className="text-sm text-gray-600">
              Total: {questions.reduce((sum, q) => sum + parseInt(q.mark || 0), 0)} marks
            </span>
          </div>

          {questions.length === 0 ? (
            <div className="text-center py-16 bg-gray-50 rounded-xl">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <DocumentTextIcon className="h-10 w-10 text-gray-400" />
              </div>
              <p className="text-lg font-semibold text-gray-600 mb-2">No questions added yet</p>
              <p className="text-gray-500 mb-6">Add questions to this exam to get started</p>
              <button
                onClick={() => navigate(`/admin/exam/update/${eid}`)}
                className="inline-flex items-center space-x-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-green-700 hover:to-emerald-700 transition-all shadow-md"
              >
                <DocumentTextIcon className="h-5 w-5" />
                <span>Add Questions</span>
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {questions.map((question, index) => (
                <div 
                  key={question.qid} 
                  className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-xl p-6 hover:shadow-md transition-all"
                >
                  {/* Question Header */}
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-4 pb-4 border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-blue-600 text-white rounded-xl flex items-center justify-center font-bold">
                        {index + 1}
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-800">Question {index + 1}</h3>
                        <p className="text-xs text-gray-500">ID: {question.qid}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-yellow-100 text-yellow-800">
                        <TrophyIcon className="h-3 w-3 mr-1" />
                        {question.mark} marks
                      </span>
                      {(question.group_title || question.group_name) && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-purple-100 text-purple-800">
                          <TagIcon className="h-3 w-3 mr-1" />
                          {question.group_title || question.group_name}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Question Text */}
                  <div 
                    className="prose max-w-none mb-4 text-gray-800 font-medium"
                    dangerouslySetInnerHTML={{ __html: question.question }} 
                  />

                  {/* Options */}
                  <div className="space-y-3 mb-4">
                    {question.op1 && (
                      <div className="flex items-start p-3 bg-white rounded-lg border border-gray-200">
                        <div className="w-7 h-7 rounded-lg flex items-center justify-center font-bold text-sm bg-gray-200 text-gray-700 mr-3 flex-shrink-0">
                          A
                        </div>
                        <div className="flex-1 text-sm text-gray-800" dangerouslySetInnerHTML={{ __html: question.op1 }} />
                      </div>
                    )}
                    {question.op2 && (
                      <div className="flex items-start p-3 bg-white rounded-lg border border-gray-200">
                        <div className="w-7 h-7 rounded-lg flex items-center justify-center font-bold text-sm bg-gray-200 text-gray-700 mr-3 flex-shrink-0">
                          B
                        </div>
                        <div className="flex-1 text-sm text-gray-800" dangerouslySetInnerHTML={{ __html: question.op2 }} />
                      </div>
                    )}
                    {question.op3 && (
                      <div className="flex items-start p-3 bg-white rounded-lg border border-gray-200">
                        <div className="w-7 h-7 rounded-lg flex items-center justify-center font-bold text-sm bg-gray-200 text-gray-700 mr-3 flex-shrink-0">
                          C
                        </div>
                        <div className="flex-1 text-sm text-gray-800" dangerouslySetInnerHTML={{ __html: question.op3 }} />
                      </div>
                    )}
                    {question.op4 && (
                      <div className="flex items-start p-3 bg-white rounded-lg border border-gray-200">
                        <div className="w-7 h-7 rounded-lg flex items-center justify-center font-bold text-sm bg-gray-200 text-gray-700 mr-3 flex-shrink-0">
                          D
                        </div>
                        <div className="flex-1 text-sm text-gray-800" dangerouslySetInnerHTML={{ __html: question.op4 }} />
                      </div>
                    )}
                  </div>

                  {/* Correct Answer */}
                  <div className="bg-green-50 border-2 border-green-200 rounded-lg p-3">
                    <div className="flex items-center space-x-2">
                      <CheckCircleIcon className="h-5 w-5 text-green-600" />
                      <span className="font-semibold text-green-700 text-sm">Correct Answer:</span>
                      <span className="font-bold text-green-800">{question.answer}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewExam;
